import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError, LOGO_URL, SOCIALS } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import { Talbiyah, GLASS, SHELL } from "@/components/Talbiyah";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Facebook, Instagram, Smartphone, Download, Share2, MoreVertical, MoonStar } from "lucide-react";


export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installModalOpen, setInstallModalOpen] = useState(false);

  React.useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setDeferredPrompt(null);
    } else {
      setInstallModalOpen(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.role === "owner" && !u?.is_admin ? "/owners" : "/inbox");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${SHELL} overflow-hidden`}>
      <PageBackground dim={0.04} />
      <Talbiyah />
      {/* Left brand panel hidden so the background artwork stays fully visible */}
      <div className="hidden">


        {/* golden halo behind logo */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[26rem] h-[26rem] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.08) 45%, transparent 70%)" }} />

        <div className="relative w-full flex flex-col items-center justify-center gap-6 p-12 text-center">
          <div className="relative">
            <div className="absolute -inset-4 rounded-full border-2 border-gold/40 animate-pulse" />
            <img src={LOGO_URL} alt="Bengal Umrah House" data-testid="login-large-logo"
              className="w-72 h-72 xl:w-80 xl:h-80 rounded-full object-cover ring-4 ring-gold shadow-[0_0_60px_rgba(212,175,55,0.45)] bg-[#031C15]" />
          </div>
          <div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <MoonStar className="w-5 h-5 text-gold" />
              <p className="eyebrow text-gold tracking-[0.25em]">Customer Care Console</p>
            </div>
            <h1 className="text-5xl font-serif font-bold text-white leading-tight drop-shadow-lg">
              Bengal Umrah<br />House Travels
            </h1>
            <div className="w-24 h-0.5 bg-gold mx-auto mt-4" />
            <p className="text-emerald-100/70 mt-4 max-w-sm text-sm leading-relaxed mx-auto">
              Sacred journeys with dignity & devotion. Unified voice, WhatsApp & SMS support in one reverent console.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="login-facebook-link"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-gold/20 border border-gold/30 flex items-center justify-center transition-colors">
              <Facebook className="w-4 h-4 text-gold" />
            </a>
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="login-instagram-link"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-gold/20 border border-gold/30 flex items-center justify-center transition-colors">
              <Instagram className="w-4 h-4 text-gold" />
            </a>
          </div>
        </div>
      </div>

      {/* Right: sign in */}
      <div className="relative z-[2] w-full max-w-[380px]">
        <form onSubmit={submit} className={`w-full fade-in ${GLASS}`} data-testid="login-form">
          <div className="hidden">
            <p className="text-[11px] eyebrow text-gold/90 tracking-[0.3em] mb-2">Talbiyah</p>
            <p className="text-2xl sm:text-[1.7rem] text-[#FFE08A] leading-[1.9] drop-shadow-[0_0_14px_rgba(255,215,100,0.8)]" style={{ fontFamily: "'Amiri', serif", letterSpacing: 0 }} dir="rtl" lang="ar">
              لَبَّيْكَ اللَّهُمَّ لَبَّيْكَ، لَبَّيْكَ لَا شَرِيكَ لَكَ لَبَّيْكَ، إِنَّ الْحَمْدَ وَالنِّعْمَةَ لَكَ وَالْمُلْكَ، لَا شَرِيكَ لَكَ
            </p>
            <p className="text-sm text-white font-semibold mt-3 leading-relaxed">Labbaik Allahumma Labbaik, Labbaik La Shareeka Laka Labbaik, Innal Hamda Wan-Ni'mata Laka Wal-Mulk, La Shareeka Lak</p>
            <p className="text-xs text-white/75 mt-2 italic leading-relaxed">Here I am, O Allah, here I am. Here I am, You have no partner, here I am. Surely all praise, grace and sovereignty belong to You. You have no partner.</p>
          </div>
          <div className="flex flex-col items-center gap-3 mb-6 text-center">
            <img src={LOGO_URL} alt="Bengal Umrah House" data-testid="login-mobile-logo"
              className="w-24 h-24 rounded-full object-cover ring-2 ring-gold/70 shadow-[0_0_40px_rgba(212,175,55,0.35)] lg:hidden" />
            <div>
              <p className="font-serif text-xl font-bold text-white">Bengal Umrah House</p>
              <p className="eyebrow text-gold text-[10px]">Customer Care Console</p>
            </div>
          </div>
          <p className="eyebrow text-gold mb-2 text-center">Welcome back</p>
          <h2 className="text-2xl font-serif font-semibold mb-6 text-center">Company Login</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-card/60" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs text-muted-foreground">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-card/60" placeholder="••••••••" required />
            </div>
          </div>

          {error && <p data-testid="login-error" className="text-sm text-destructive mt-4">{error}</p>}

          <Button type="submit" data-testid="login-submit-button" disabled={loading}
            className="w-full mt-6 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gold-glow">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Button>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            Contact us: bengalumrahhouse@gmail.com · Office staff use the Office Staff Login below.
          </p>

          <Link to="/staff-login" data-testid="staff-access-link" className="mt-4 flex items-center justify-center gap-2 w-full rounded-lg border border-gold/40 bg-gold/10 py-2.5 text-sm text-gold font-semibold hover:bg-gold/20 transition-colors">
            Office Staff Login
          </Link>

          <div className="mt-4 text-center">
            <Link to="/agent" data-testid="agent-access-link" className="text-xs text-gold/90 hover:text-gold underline underline-offset-2 font-medium">
              Associate partner or member? Login here
            </Link>
          </div>

          <div className="mt-2 text-center">
            <Link to="/customer-login" data-testid="customer-access-link" className="text-xs text-emerald-100/70 hover:text-gold underline underline-offset-2">
              Umrah pilgrim or job customer? Login here
            </Link>
          </div>

          <div className="mt-6 pt-5 border-t border-border/50 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Mobile access:</span>
            <Button type="button" variant="outline" size="sm" onClick={handleInstallClick}
              data-testid="login-install-app-btn" className="border-gold/40 text-gold hover:bg-gold/15 gap-1.5 text-xs h-8">
              <Download className="w-3.5 h-3.5" /> Install App Shortcut
            </Button>
          </div>

          <Dialog open={installModalOpen} onOpenChange={setInstallModalOpen}>
            <DialogContent className="bg-[#05261D] border-gold/30 text-white max-w-md" data-testid="login-install-modal">
              <DialogHeader>
                <DialogTitle className="font-serif text-gold flex items-center gap-2 text-lg">
                  <Smartphone className="w-5 h-5 text-gold" /> Install Bengal Umrah App
                </DialogTitle>
                <DialogDescription className="text-emerald-100/70 text-xs">
                  Add Bengal Umrah House Care Console directly to your phone's Home Screen as an app icon.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3.5 my-2 text-sm">
                <div className="p-3 rounded-lg bg-card/60 border border-border/70">
                  <p className="font-semibold text-gold flex items-center gap-1.5 text-xs uppercase tracking-wider mb-1.5">
                    <MoreVertical className="w-3.5 h-3.5" /> Android (Chrome)
                  </p>
                  <p className="text-xs text-muted-foreground">Tap Chrome's <strong>3 dots (⋮)</strong> → Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</p>
                </div>
                <div className="p-3 rounded-lg bg-card/60 border border-border/70">
                  <p className="font-semibold text-gold flex items-center gap-1.5 text-xs uppercase tracking-wider mb-1.5">
                    <Share2 className="w-3.5 h-3.5" /> iPhone / iPad (Safari)
                  </p>
                  <p className="text-xs text-muted-foreground">Tap Safari's <strong>Share button</strong> → Tap <strong>"Add to Home Screen"</strong> → Tap <strong>Add</strong>.</p>
                </div>
              </div>
              <Button type="button" onClick={() => setInstallModalOpen(false)} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-xs">
                Got it
              </Button>
            </DialogContent>
          </Dialog>
        </form>
      </div>
    </div>
  );
}
